import AddIcon from '@mui/icons-material/Add';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventIcon from '@mui/icons-material/Event';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LinkIcon from '@mui/icons-material/Link';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import theme from '../../theme';
import { usePrototype } from './PrototypeContext';

// Types
interface ActionDetail {
  id: string;
  priority: number;
  taskId: string;
  title: string;
  description: string;
  completionType: string;
  attestType: string;
  rewardType: string;
  amount: string;
  startDate: string;
  expiryDate: string;
  recurring: boolean;
  recurringInterval: string;
  eligibilityDate: string;
  secondaryCta: string;
  link: string;
  cohorts: string[];
  category: string;
  storyblokId?: number;
}

const STORYBLOK_SPACE_ID = '572723165993589';
const getStoryblokEditUrl = (storyId: number) =>
  `https://app.storyblok.com/#/me/spaces/${STORYBLOK_SPACE_ID}/stories/0/0/${storyId}`;

interface Collection {
  id: string;
  category: string;
  collectionName: string;
  assignedActions: number;
  enrolled: number;
  cohorts: string[];
  avgCompletion: string;
}

// Sample data for All Actions with full details
const actionsData: ActionDetail[] = [
  {
    id: '1',
    priority: 17,
    taskId: '7382J',
    title: 'Step it up',
    description:
      'Step your way to better health, less stress, and more rewards. Walk 200,000 steps this month.',
    completionType: 'Fitness Data Tracking',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$10',
    startDate: '12/30/25',
    expiryDate: '12/30/26',
    recurring: true,
    recurringInterval: 'Monthly',
    eligibilityDate: '1/1/26',
    secondaryCta: 'Learn more',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/h...',
    cohorts: ['18 years and above', 'Signed up this month'],
    category: 'Healthy Eating',
    storyblokId: 162992559403894,
  },
  {
    id: '2',
    priority: 16,
    taskId: 'Z9X2C',
    title: 'Eat Well to Fuel Your Body',
    description:
      'Learn about proper nutrition and how to fuel your body for optimal health and performance.',
    completionType: 'Hybrid',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$20',
    startDate: '10/25/25',
    expiryDate: '10/25/26',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '10/25/25',
    secondaryCta: 'Learn more',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/nutrition',
    cohorts: ['All customers'],
    category: 'Healthy Eating',
    storyblokId: 163010006663043,
  },
  {
    id: '3',
    priority: 15,
    taskId: 'R4Q1L',
    title: 'Try Our Healthy Recipes',
    description:
      'Discover delicious and nutritious recipes that will help you maintain a healthy lifestyle. Try new dishes and earn rewards for your healthy choices.',
    completionType: 'Self Report',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$5.00',
    startDate: '10/25/25',
    expiryDate: '10/25/26',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '10/25/25',
    secondaryCta: 'Learn more',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/recipes',
    cohorts: ['18 years and above'],
    category: 'Healthy Eating',
  },
  {
    id: '4',
    priority: 14,
    taskId: 'W7Y5T',
    title: 'Set Up Your Device to Track...',
    description:
      'Connect your fitness device to start tracking your health metrics and earning rewards.',
    completionType: 'Self Report',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$5.00',
    startDate: '11/15/25',
    expiryDate: '11/15/26',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '11/15/25',
    secondaryCta: 'Get started',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/devices',
    cohorts: ['Active members'],
    category: 'Fitness & Exercise',
  },
  {
    id: '5',
    priority: 13,
    taskId: 'P2A8K',
    title: 'Hydrate to Feel Your Best',
    description: 'Stay hydrated throughout the day to maintain energy and focus.',
    completionType: 'Self Report',
    attestType: 'SELF ATTEST',
    rewardType: 'Entries',
    amount: '10',
    startDate: '11/22/25',
    expiryDate: '11/22/26',
    recurring: true,
    recurringInterval: 'Daily',
    eligibilityDate: '11/22/25',
    secondaryCta: 'Learn more',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/hydration',
    cohorts: ['All customers'],
    category: 'Healthy Eating',
  },
  {
    id: '6',
    priority: 12,
    taskId: 'S6B3N',
    title: 'Eat Well to Fuel Your Body',
    description: 'Learn about proper nutrition and how to fuel your body for optimal health.',
    completionType: 'Hybrid',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$15.00',
    startDate: '01/05/26',
    expiryDate: '01/05/27',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '01/05/26',
    secondaryCta: 'Learn more',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/nutrition',
    cohorts: ['Premium members'],
    category: 'Healthy Eating',
  },
  {
    id: '7',
    priority: 11,
    taskId: 'H1J9G',
    title: "Get Your Zzz's",
    description: 'Improve your sleep habits for better health and wellbeing.',
    completionType: 'Self Report',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$10.00',
    startDate: '03/10/26',
    expiryDate: '03/10/27',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '03/10/26',
    secondaryCta: 'Learn more',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/sleep',
    cohorts: ['18 years and above'],
    category: 'Wellness Journey',
  },
  {
    id: '8',
    priority: 10,
    taskId: 'M3N6X',
    title: 'Step it up',
    description: 'Increase your daily step count for better cardiovascular health.',
    completionType: 'Fitness Data Tracking',
    attestType: 'SELF ATTEST',
    rewardType: 'Entries',
    amount: '10',
    startDate: '02/18/26',
    expiryDate: '02/18/27',
    recurring: true,
    recurringInterval: 'Weekly',
    eligibilityDate: '02/18/26',
    secondaryCta: 'Track now',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/steps',
    cohorts: ['Active members'],
    category: 'Fitness & Exercise',
  },
  {
    id: '9',
    priority: 9,
    taskId: 'Y8C4V',
    title: 'Try Our Healthy Recipes',
    description: 'Explore healthy recipe options for every meal of the day.',
    completionType: 'Self Report',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$5.00',
    startDate: '04/20/26',
    expiryDate: '04/20/27',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '04/20/26',
    secondaryCta: 'View recipes',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/recipes',
    cohorts: ['All customers'],
    category: 'Healthy Eating',
  },
  {
    id: '10',
    priority: 8,
    taskId: 'U5I2Q',
    title: 'Start Your Wellness Coaching',
    description: 'Get personalized guidance from our wellness coaches.',
    completionType: 'Self Report',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$15.00',
    startDate: '05/15/26',
    expiryDate: '05/15/27',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '05/15/26',
    secondaryCta: 'Book session',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/coaching',
    cohorts: ['Premium members'],
    category: 'Wellness Journey',
  },
  {
    id: '11',
    priority: 7,
    taskId: 'O9Z7R',
    title: 'Meditate To boost Your Well...',
    description: 'Practice mindfulness and meditation for mental clarity.',
    completionType: 'Hybrid',
    attestType: 'SELF ATTEST',
    rewardType: 'Entries',
    amount: '10',
    startDate: '06/01/26',
    expiryDate: '06/01/27',
    recurring: true,
    recurringInterval: 'Daily',
    eligibilityDate: '06/01/26',
    secondaryCta: 'Start meditating',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/meditation',
    cohorts: ['All customers'],
    category: 'Wellness Journey',
  },
  {
    id: '12',
    priority: 6,
    taskId: 'D2E5W',
    title: 'Strengthen Your Body',
    description: 'Build strength and improve your fitness with guided exercises.',
    completionType: 'Fitness Data Tracking',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$20.00',
    startDate: '07/25/26',
    expiryDate: '07/25/27',
    recurring: true,
    recurringInterval: 'Weekly',
    eligibilityDate: '07/25/26',
    secondaryCta: 'Start workout',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/strength',
    cohorts: ['Active members'],
    category: 'Fitness & Exercise',
  },
  {
    id: '13',
    priority: 5,
    taskId: 'F7T1S',
    title: 'Set Up Your Device To Track...',
    description: 'Connect your wearable device to track your health metrics.',
    completionType: 'Self Report',
    attestType: 'SELF ATTEST',
    rewardType: 'Dollars',
    amount: '$5.00',
    startDate: '08/30/26',
    expiryDate: '08/30/27',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '08/30/26',
    secondaryCta: 'Connect device',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/devices',
    cohorts: ['New <30d'],
    category: 'Fitness & Exercise',
  },
  {
    id: '14',
    priority: 4,
    taskId: 'B4L8P',
    title: 'Eat Well to Fuel Your Body',
    description: 'Healthy eating habits for a healthier you.',
    completionType: 'Hybrid',
    attestType: 'SELF ATTEST',
    rewardType: 'Entries',
    amount: '10',
    startDate: '09/10/26',
    expiryDate: '09/10/27',
    recurring: false,
    recurringInterval: '',
    eligibilityDate: '09/10/26',
    secondaryCta: 'Learn more',
    link: 'https://healthy.kaiserpermanente.org/health-wellness/nutrition',
    cohorts: ['All customers'],
    category: 'Healthy Eating',
  },
];

// Sample data for Collections
const collectionsData: Collection[] = [
  {
    id: '1',
    category: 'Health Adventure',
    collectionName: 'Healthy Eating',
    assignedActions: 12,
    enrolled: 24,
    cohorts: ['New <30d', '+2'],
    avgCompletion: '50%',
  },
  {
    id: '2',
    category: 'Health Adventure',
    collectionName: 'Fitness & Exercise',
    assignedActions: 5,
    enrolled: 31,
    cohorts: ['18 years+', '+1'],
    avgCompletion: '100%',
  },
];

// Available cohorts for selection
const availableCohorts = [
  '18 years and above',
  'Signed up this month',
  'New <30d',
  'Active members',
  'Premium members',
  'All customers',
];

// Secondary CTA options
const secondaryCtaOptions = [
  'Learn more',
  'Get started',
  'Track now',
  'View recipes',
  'Book session',
  'Start meditating',
  'Start workout',
  'Connect device',
];

// Recurring interval options
const recurringIntervalOptions = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'];

// Reward type options
const rewardTypeOptions = ['Dollars', 'Entries', 'Points'];

const PrototypeActions: React.FC = () => {
  const { isMasterTenant, selectedTenant } = usePrototype();

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(1);

  // Tenant-specific actions state
  const [tenantActions, setTenantActions] = useState<ActionDetail[]>([]);

  // Import from catalog dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedImportActions, setSelectedImportActions] = useState<string[]>([]);
  const [expandedImportCategories, setExpandedImportCategories] = useState<string[]>([]);

  // CSV Import dialog state
  const [csvImportStep, setCsvImportStep] = useState<'template' | 'upload' | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportColumns, setExportColumns] = useState<string[]>([
    'Title',
    'Priority',
    'Collection',
    'Reward Type',
    'Amount',
    'Start Date',
    'Expiry Date',
    'Task ID',
  ]);

  // Available export columns
  const allExportColumns = [
    'Title',
    'Priority',
    'Description',
    'Link',
    'Collection',
    'Completion Type',
    'Reward Type',
    'Amount',
    'Start Date',
    'Expiry Date',
    'Task ID',
    'Cohorts',
  ];

  // Create new menu and dialog state
  const [createNewMenuAnchor, setCreateNewMenuAnchor] = useState<null | HTMLElement>(null);
  const [createActionDialogOpen, setCreateActionDialogOpen] = useState(false);
  const [selectedCompletionType, setSelectedCompletionType] = useState<string | null>(null);

  // Self Report input type dialog state
  const [selfReportDialogOpen, setSelfReportDialogOpen] = useState(false);
  const [selectedInputType, setSelectedInputType] = useState<string | null>(null);

  // Create action form state
  const [isCreatingAction, setIsCreatingAction] = useState(false);
  const [createActionCollection, setCreateActionCollection] = useState('Not Assigned');
  const [createTaskId, setCreateTaskId] = useState('');
  const [createTitle, setCreateTitle] = useState('');
  const [createPriority, setCreatePriority] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createSecondaryCta, setCreateSecondaryCta] = useState('Learn more');
  const [createLink, setCreateLink] = useState('');
  const [createStartDate, setCreateStartDate] = useState('');
  const [createExpiryDate, setCreateExpiryDate] = useState('');
  const [createRecurring, setCreateRecurring] = useState(false);
  const [createRecurringInterval, setCreateRecurringInterval] = useState('');
  const [createEligibilityDate, setCreateEligibilityDate] = useState('');
  const [createCohorts, setCreateCohorts] = useState<string[]>(['All customers']);
  const [createRewardType, setCreateRewardType] = useState('Dollars');
  const [createRewardAmount, setCreateRewardAmount] = useState('');

  // Upload Image specific fields
  const [uploadUnitOfMeasurement, setUploadUnitOfMeasurement] = useState('Upload Photos');
  const [uploadMaxValue, setUploadMaxValue] = useState('2');
  const [uploadCompletionThreshold, setUploadCompletionThreshold] = useState('2+');
  const [uploadModalHeader, setUploadModalHeader] = useState('');
  const [uploadModalDescription, setUploadModalDescription] = useState('');
  const [uploadJsonSchema, setUploadJsonSchema] = useState('');

  // Row action menu state
  const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);

  // Edit/Duplicate action state
  const [editMode, setEditMode] = useState<'edit' | 'duplicate' | null>(null);
  const [editingAction, setEditingAction] = useState<ActionDetail | null>(null);

  // Duplicate confirmation dialog state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [actionToDuplicate, setActionToDuplicate] = useState<ActionDetail | null>(null);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSecondaryCta, setEditSecondaryCta] = useState('');
  const [editLink, setEditLink] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [editRecurring, setEditRecurring] = useState(false);
  const [editRecurringInterval, setEditRecurringInterval] = useState('');
  const [editEligibilityDate, setEditEligibilityDate] = useState('');
  const [editCohorts, setEditCohorts] = useState<string[]>([]);
  const [editRewardType, setEditRewardType] = useState('');
  const [editRewardAmount, setEditRewardAmount] = useState('');

  // Create collection form state
  const [isCreating, setIsCreating] = useState(false);
  const [newCollectionTitle, setNewCollectionTitle] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [newCollectionCategory, setNewCollectionCategory] = useState('Health Adventure');
  const [selectedCohorts, setSelectedCohorts] = useState<string[]>([]);

  // Collections state
  const [collections, setCollections] = useState<Collection[]>(collectionsData);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Action detail menu (three dots in edit view)
  const [actionDetailMenuAnchor, setActionDetailMenuAnchor] = useState<null | HTMLElement>(null);

  // Filter panel state
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilterCategory, setActiveFilterCategory] = useState<string>('Completion type');

  // Filter values state
  const [filterCompletionTypes, setFilterCompletionTypes] = useState<string[]>([]);
  const [filterRewardType, setFilterRewardType] = useState('');
  const [filterRewardMin, setFilterRewardMin] = useState('');
  const [filterRewardMax, setFilterRewardMax] = useState('');
  const [filterDatePreset, setFilterDatePreset] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCollections, setFilterCollections] = useState<string[]>([]);
  const [filterCohortsList, setFilterCohortsList] = useState<string[]>([]);

  // Reset actions when tenant changes
  useEffect(() => {
    if (isMasterTenant) {
      setTenantActions(actionsData);
    } else {
      setTenantActions([]);
    }
    setSelectedImportActions([]);
    setExpandedImportCategories([]);
  }, [selectedTenant, isMasterTenant]);

  // Get current actions based on tenant
  const currentActions = isMasterTenant ? actionsData : tenantActions;

  // Group master actions by category for import dialog
  const masterActionsByCategory = actionsData.reduce(
    (acc, action) => {
      const category = action.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(action);
      return acc;
    },
    {} as Record<string, ActionDetail[]>
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setIsCreating(false);
    setEditMode(null);
    setEditingAction(null);
  };

  const handleRowMenuOpen = (event: React.MouseEvent<HTMLElement>, actionId: string) => {
    event.stopPropagation();
    setRowMenuAnchor(event.currentTarget);
    setSelectedActionId(actionId);
  };

  const handleRowMenuClose = () => {
    setRowMenuAnchor(null);
    setSelectedActionId(null);
  };

  const handleEditAction = () => {
    const action = actionsData.find((a) => a.id === selectedActionId);
    if (action) {
      setEditingAction(action);
      setEditMode('edit');
      populateEditForm(action);
    }
    handleRowMenuClose();
  };

  const handleDuplicateAction = () => {
    const action = actionsData.find((a) => a.id === selectedActionId);
    if (action) {
      setActionToDuplicate(action);
      setDuplicateDialogOpen(true);
    }
    handleRowMenuClose();
  };

  const handleConfirmDuplicate = () => {
    if (actionToDuplicate) {
      setEditingAction(actionToDuplicate);
      setEditMode('duplicate');
      populateEditForm(actionToDuplicate, true);
    }
    setDuplicateDialogOpen(false);
    setActionToDuplicate(null);
  };

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
    setActionToDuplicate(null);
  };

  const populateEditForm = (action: ActionDetail, isDuplicate = false) => {
    setEditTitle(action.title);
    // For duplicate, assign next priority level
    setEditPriority(
      isDuplicate
        ? String(Math.max(...actionsData.map((a) => a.priority)) + 1)
        : String(action.priority)
    );
    setEditDescription(action.description);
    setEditSecondaryCta(action.secondaryCta);
    setEditLink(action.link);
    setEditStartDate(action.startDate);
    setEditExpiryDate(action.expiryDate);
    setEditRecurring(action.recurring);
    setEditRecurringInterval(action.recurringInterval);
    setEditEligibilityDate(action.eligibilityDate);
    setEditCohorts(isDuplicate ? ['All customers'] : [...action.cohorts]);
    setEditRewardType(action.rewardType);
    setEditRewardAmount(action.amount);
  };

  const handleSaveAction = () => {
    setSnackbarMessage(
      editMode === 'duplicate' ? 'Action duplicated successfully' : 'Action saved successfully'
    );
    setSnackbarOpen(true);
    setEditMode(null);
    setEditingAction(null);
  };

  const handleCancelEdit = () => {
    setEditMode(null);
    setEditingAction(null);
  };

  const handleAddEditCohort = (cohort: string) => {
    if (!editCohorts.includes(cohort)) {
      setEditCohorts([...editCohorts, cohort]);
    }
  };

  const handleRemoveEditCohort = (cohort: string) => {
    setEditCohorts(editCohorts.filter((c) => c !== cohort));
  };

  const handleCreateNew = () => {
    if (activeTab === 1) {
      setIsCreating(true);
      setNewCollectionTitle('');
      setNewCollectionDescription('');
      setNewCollectionCategory('Health Adventure');
      setSelectedCohorts([]);
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
  };

  const handleCreateCollection = () => {
    if (!newCollectionTitle.trim()) return;

    const newCollection: Collection = {
      id: `${collections.length + 1}`,
      category: newCollectionCategory,
      collectionName: newCollectionTitle,
      assignedActions: 0,
      enrolled: 0,
      cohorts:
        selectedCohorts.length > 0
          ? [
              selectedCohorts[0].substring(0, 10) + (selectedCohorts.length > 1 ? '' : ''),
              ...(selectedCohorts.length > 1 ? [`+${selectedCohorts.length - 1}`] : []),
            ]
          : [],
      avgCompletion: '0%',
    };

    setCollections([...collections, newCollection]);
    setIsCreating(false);
    setSnackbarMessage('Collection created successfully');
    setSnackbarOpen(true);
  };

  const handleAddCohort = (cohort: string) => {
    if (!selectedCohorts.includes(cohort)) {
      setSelectedCohorts([...selectedCohorts, cohort]);
    }
  };

  const handleRemoveCohort = (cohort: string) => {
    setSelectedCohorts(selectedCohorts.filter((c) => c !== cohort));
  };

  // Filter handlers
  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterCategoryClick = (category: string) => {
    setActiveFilterCategory(category);
  };

  const handleCompletionTypeToggle = (type: string) => {
    setFilterCompletionTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleCollectionToggle = (collection: string) => {
    setFilterCollections((prev) =>
      prev.includes(collection) ? prev.filter((c) => c !== collection) : [...prev, collection]
    );
  };

  const handleCohortFilterToggle = (cohort: string) => {
    setFilterCohortsList((prev) =>
      prev.includes(cohort) ? prev.filter((c) => c !== cohort) : [...prev, cohort]
    );
  };

  const handleResetFilters = () => {
    setFilterCompletionTypes([]);
    setFilterRewardType('');
    setFilterRewardMin('');
    setFilterRewardMax('');
    setFilterDatePreset('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterCollections([]);
    setFilterCohortsList([]);
  };

  const handleApplyFilters = () => {
    // In a real app, this would filter the data
    setSnackbarMessage('Filters applied');
    setSnackbarOpen(true);
    handleFilterClose();
  };

  const getFilterResultsCount = () => {
    // Mock count based on selected filters
    const hasFilters =
      filterCompletionTypes.length > 0 ||
      filterRewardType ||
      filterCollections.length > 0 ||
      filterCohortsList.length > 0 ||
      filterDateFrom ||
      filterDateTo;
    return hasFilters ? 10 : currentActions.length;
  };

  // Import from catalog handlers
  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
    setSelectedImportActions([]);
    setExpandedImportCategories([]);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setSelectedImportActions([]);
    setExpandedImportCategories([]);
  };

  const handleToggleImportCategory = (category: string) => {
    setExpandedImportCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const handleToggleImportAction = (actionId: string) => {
    setSelectedImportActions((prev) =>
      prev.includes(actionId) ? prev.filter((id) => id !== actionId) : [...prev, actionId]
    );
  };

  const handleToggleImportCategoryAll = (category: string) => {
    const categoryActions = masterActionsByCategory[category] || [];
    const categoryActionIds = categoryActions.map((a) => a.id);
    const allSelected = categoryActionIds.every((id) => selectedImportActions.includes(id));

    if (allSelected) {
      setSelectedImportActions((prev) => prev.filter((id) => !categoryActionIds.includes(id)));
    } else {
      setSelectedImportActions((prev) => [...new Set([...prev, ...categoryActionIds])]);
    }
  };

  const isCategoryFullySelected = (category: string) => {
    const categoryActions = masterActionsByCategory[category] || [];
    return (
      categoryActions.length > 0 &&
      categoryActions.every((a) => selectedImportActions.includes(a.id))
    );
  };

  const isCategoryPartiallySelected = (category: string) => {
    const categoryActions = masterActionsByCategory[category] || [];
    const selectedCount = categoryActions.filter((a) =>
      selectedImportActions.includes(a.id)
    ).length;
    return selectedCount > 0 && selectedCount < categoryActions.length;
  };

  const handleImportActions = () => {
    // Import selected actions
    const actionsToImport = actionsData.filter((a) => selectedImportActions.includes(a.id));

    // Add imported actions to tenant actions (avoiding duplicates)
    setTenantActions((prev) => {
      const existingIds = prev.map((a) => a.id);
      const newActions = actionsToImport.filter((a) => !existingIds.includes(a.id));
      return [...prev, ...newActions];
    });

    setSnackbarMessage(`${actionsToImport.length} action(s) imported successfully`);
    setSnackbarOpen(true);
    handleCloseImportDialog();
  };

  // CSV Import handlers
  const handleOpenCsvImport = () => {
    setCsvImportStep('template');
    setUploadedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadComplete(false);
  };

  const handleCloseCsvImport = () => {
    setCsvImportStep(null);
    setUploadedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadComplete(false);
  };

  const handleDownloadTemplate = () => {
    // Simulate template download
    setSnackbarMessage('Template downloaded');
    setSnackbarOpen(true);
  };

  const handleProceedToUpload = () => {
    setCsvImportStep('upload');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      simulateUpload();
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.csv')) {
      setUploadedFile(file);
      simulateUpload();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const simulateUpload = () => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadComplete(false);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          setUploadComplete(true);
          return 100;
        }
        return prev + 25;
      });
    }, 500);
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadComplete(false);
  };

  const handleCsvImportComplete = () => {
    // Simulate importing actions from CSV
    setSnackbarMessage('27 Actions imported successfully');
    setSnackbarOpen(true);
    handleCloseCsvImport();
  };

  const handleExportActions = () => {
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
  };

  const handleToggleExportColumn = (column: string) => {
    setExportColumns((prev) =>
      prev.includes(column) ? prev.filter((c) => c !== column) : [...prev, column]
    );
  };

  const handleToggleAllExportColumns = () => {
    if (exportColumns.length === allExportColumns.length) {
      setExportColumns([]);
    } else {
      setExportColumns([...allExportColumns]);
    }
  };

  const handleExportToCsv = () => {
    const count = currentActions.length;
    setSnackbarMessage(`${count} Actions exported successfully`);
    setSnackbarOpen(true);
    setExportDialogOpen(false);
  };

  // Create new handlers
  const handleCreateNewMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCreateNewMenuAnchor(event.currentTarget);
  };

  const handleCreateNewMenuClose = () => {
    setCreateNewMenuAnchor(null);
  };

  const handleCreateNewAction = () => {
    handleCreateNewMenuClose();
    setCreateActionDialogOpen(true);
    setSelectedCompletionType(null);
  };

  const handleCreateNewCollection = () => {
    handleCreateNewMenuClose();
    setIsCreating(true);
    setActiveTab(1);
  };

  const handleCloseCreateActionDialog = () => {
    setCreateActionDialogOpen(false);
    setSelectedCompletionType(null);
  };

  const handleSelectCompletionType = (type: string) => {
    setSelectedCompletionType(type);
    setCreateActionDialogOpen(false);
    if (type === 'Self Report') {
      setSelfReportDialogOpen(true);
    } else {
      // For Fitness Data Tracking and Hybrid, directly open form (can be extended later)
      setSnackbarMessage(`Creating ${type} action...`);
      setSnackbarOpen(true);
    }
  };

  // Self Report input type handlers
  const handleCloseSelfReportDialog = () => {
    setSelfReportDialogOpen(false);
    setSelectedInputType(null);
  };

  const handleBackToCompletionType = () => {
    setSelfReportDialogOpen(false);
    setCreateActionDialogOpen(true);
  };

  const handleSelectInputType = (inputType: string) => {
    setSelectedInputType(inputType);
  };

  const handleCreateSelfReportAction = () => {
    if (!selectedInputType) return;
    setSelfReportDialogOpen(false);
    // Generate a random task ID
    const taskId = Math.random().toString(36).substring(2, 7).toUpperCase();
    setCreateTaskId(taskId);
    // Set default priority
    const nextPriority = Math.max(...actionsData.map((a) => a.priority)) + 1;
    setCreatePriority(String(nextPriority));
    // Reset form fields
    setCreateTitle('');
    setCreateDescription('');
    setCreateSecondaryCta('Learn more');
    setCreateLink('');
    setCreateStartDate('');
    setCreateExpiryDate('');
    setCreateRecurring(false);
    setCreateRecurringInterval('');
    setCreateEligibilityDate('');
    setCreateCohorts(['All customers']);
    setCreateRewardType('Dollars');
    setCreateRewardAmount('');
    setCreateActionCollection('Not Assigned');
    // Show create action form
    setIsCreatingAction(true);
  };

  const handleCancelCreateAction = () => {
    setIsCreatingAction(false);
    setSelectedCompletionType(null);
    setSelectedInputType(null);
  };

  const handleSaveNewAction = () => {
    setSnackbarMessage('Action created successfully');
    setSnackbarOpen(true);
    setIsCreatingAction(false);
    setSelectedCompletionType(null);
    setSelectedInputType(null);
  };

  const isCreateFormValid = () => {
    return (
      createTitle.trim() &&
      createPriority &&
      createDescription.trim() &&
      createRewardType &&
      createRewardAmount
    );
  };

  const handleAddCreateCohort = (cohort: string) => {
    if (!createCohorts.includes(cohort)) {
      setCreateCohorts([...createCohorts, cohort]);
    }
  };

  const handleRemoveCreateCohort = (cohort: string) => {
    setCreateCohorts(createCohorts.filter((c) => c !== cohort));
  };

  const getBreadcrumb = () => {
    const base = 'Sunny Benefits > Actions';
    if (isCreatingAction) {
      return `${base} > All Actions > Create new`;
    }
    if (editMode && editingAction) {
      return `${base} > All Actions > ${editingAction.title}`;
    }
    if (activeTab === 0) return `${base} > All Actions`;
    if (activeTab === 1) {
      if (isCreating) return `${base} > Collections > Create new`;
      return `${base} > Collections`;
    }
    return `${base} > History`;
  };

  const isFormValid = () => {
    return (
      editTitle.trim() &&
      editPriority &&
      editDescription.trim() &&
      editRewardType &&
      editRewardAmount
    );
  };

  const renderEditActionForm = () => {
    if (!editingAction) return null;

    return (
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          p: 3,
          border: '1px solid #EFF0F0',
        }}
      >
        {/* Header */}
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Typography variant="h5" fontWeight={600}>
                {editingAction.title}
              </Typography>
              <Chip
                label={editingAction.category}
                size="small"
                sx={{
                  bgcolor: theme.palette.accentsYellowA.light,
                  color: theme.palette.accentsYellowA.dark,
                  fontWeight: 500,
                }}
                deleteIcon={<span style={{ fontSize: 10, marginLeft: 2 }}>▼</span>}
                onDelete={() => {}}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              TASK ID {editingAction.taskId} &bull;{' '}
              {editingAction.completionType.toUpperCase().replace(' ', ' ')} &bull;{' '}
              {editingAction.attestType}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {editingAction.storyblokId != null && (
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon />}
                href={getStoryblokEditUrl(editingAction.storyblokId)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: '#E0E0E0',
                  color: theme.palette.textDarkPrimary.primary,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Edit in CMS
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                setEditMode('duplicate');
                populateEditForm(editingAction, true);
              }}
              sx={{
                borderColor: '#E0E0E0',
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Duplicate
            </Button>
            <IconButton
              onClick={(e) => setActionDetailMenuAnchor(e.currentTarget)}
              sx={{ border: '1px solid #E0E0E0', borderRadius: 1 }}
            >
              <MoreHorizIcon />
            </IconButton>
            <Menu
              anchorEl={actionDetailMenuAnchor}
              open={Boolean(actionDetailMenuAnchor)}
              onClose={() => setActionDetailMenuAnchor(null)}
            >
              <MenuItem onClick={() => setActionDetailMenuAnchor(null)}>
                <RemoveCircleOutlineIcon sx={{ mr: 1, fontSize: 20 }} /> Deprecate
              </MenuItem>
              <MenuItem onClick={() => setActionDetailMenuAnchor(null)}>
                <RestoreIcon sx={{ mr: 1, fontSize: 20 }} /> Restore
              </MenuItem>
              <MenuItem onClick={() => setActionDetailMenuAnchor(null)}>
                <DeleteOutlineIcon sx={{ mr: 1, fontSize: 20 }} /> Delete
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Form Fields */}
        <Box sx={{ mt: 3 }}>
          {/* Title and Priority */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Title <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
              />
            </Box>
            <Box sx={{ width: 120 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Priority level <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
              />
            </Box>
          </Box>

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Description: <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
            />
          </Box>

          {/* Secondary CTA and Link */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Secondary CTA <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Select
                fullWidth
                value={editSecondaryCta}
                onChange={(e) => setEditSecondaryCta(e.target.value)}
                sx={{ bgcolor: '#FFFFFF' }}
              >
                {secondaryCtaOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Link <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={editLink}
                onChange={(e) => setEditLink(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon sx={{ color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
              />
            </Box>
          </Box>

          {/* Dates and Recurring */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3, alignItems: 'flex-end' }}>
            <Box sx={{ width: 160 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Start date <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
              />
            </Box>
            <Typography sx={{ pb: 1 }}>&ndash;</Typography>
            <Box sx={{ width: 160 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Expiry date <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={editExpiryDate}
                onChange={(e) => setEditExpiryDate(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Recurring <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Switch
                checked={editRecurring}
                onChange={(e) => setEditRecurring(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.accentsYellowA.main,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: theme.palette.accentsYellowA.main,
                  },
                }}
              />
              <Typography variant="body2">Yes</Typography>
            </Box>
            {editRecurring && (
              <Select
                value={editRecurringInterval}
                onChange={(e) => setEditRecurringInterval(e.target.value)}
                size="small"
                sx={{ minWidth: 120, bgcolor: '#FFFFFF' }}
              >
                {recurringIntervalOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          {/* Eligibility Date */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Eligibility for completion starts <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              value={editEligibilityDate}
              onChange={(e) => setEditEligibilityDate(e.target.value)}
              sx={{ width: 160, '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Cohorts Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Cohorts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Assign cohort(s)
            </Typography>
            <Select
              size="small"
              displayEmpty
              value=""
              onChange={(e) => handleAddEditCohort(e.target.value as string)}
              sx={{ minWidth: 200, mb: 2 }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {availableCohorts
                .filter((c) => !editCohorts.includes(c))
                .map((cohort) => (
                  <MenuItem key={cohort} value={cohort}>
                    {cohort}
                  </MenuItem>
                ))}
            </Select>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {editCohorts.map((cohort) => (
                <Chip
                  key={cohort}
                  label={cohort}
                  onDelete={() => handleRemoveEditCohort(cohort)}
                  sx={{
                    bgcolor: '#F5F5F5',
                    '& .MuiChip-deleteIcon': {
                      fontSize: 18,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Rewards Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Rewards
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Reward type <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Select
                  value={editRewardType}
                  onChange={(e) => setEditRewardType(e.target.value)}
                  sx={{ minWidth: 180, bgcolor: '#FFFFFF' }}
                >
                  {rewardTypeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Reward amount <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  value={editRewardAmount}
                  onChange={(e) => setEditRewardAmount(e.target.value)}
                  sx={{ width: 180, '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
                />
              </Box>
            </Box>
          </Box>

          {/* Save Button */}
          <Button
            variant="contained"
            onClick={handleSaveAction}
            disabled={!isFormValid()}
            sx={{
              bgcolor: isFormValid() ? theme.palette.accentsYellowA.main : '#E0E0E0',
              color: isFormValid() ? theme.palette.textDarkPrimary.primary : '#9CA3AF',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                bgcolor: isFormValid() ? theme.palette.accentsYellowA.dark : '#E0E0E0',
              },
              '&.Mui-disabled': {
                bgcolor: '#E0E0E0',
                color: '#9CA3AF',
              },
            }}
          >
            Save action
          </Button>
        </Box>
      </Box>
    );
  };

  const renderCreateActionForm = () => {
    const inputTypeLabel =
      selectedInputType === 'Self Attest'
        ? 'SELF ATTEST'
        : selectedInputType === 'Upload Image'
          ? 'UPLOAD IMAGE'
          : selectedInputType === 'Progress Dial'
            ? 'PROGRESS DIAL'
            : selectedInputType === 'Completion date'
              ? 'COMPLETION DATE'
              : '';

    const isUploadImage = selectedInputType === 'Upload Image';
    const maxVal = parseInt(uploadMaxValue) || 2;

    // Render the circular progress dial for Upload Image
    const renderProgressDial = () => {
      const tickCount = 30;
      const startAngle = 220; // Start angle in degrees (bottom left)
      const endAngle = -40; // End angle in degrees (bottom right)
      const angleRange = startAngle - endAngle;
      const radius = 70;
      const centerX = 100;
      const centerY = 95;

      const ticks = [];
      for (let i = 0; i < tickCount; i++) {
        const angle = startAngle - (i / (tickCount - 1)) * angleRange;
        const rad = (angle * Math.PI) / 180;
        const innerRadius = radius - 8;
        const outerRadius = radius + 8;
        const x1 = centerX + innerRadius * Math.cos(rad);
        const y1 = centerY - innerRadius * Math.sin(rad);
        const x2 = centerX + outerRadius * Math.cos(rad);
        const y2 = centerY - outerRadius * Math.sin(rad);
        ticks.push(
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#D1D5DB"
            strokeWidth="4"
            strokeLinecap="round"
          />
        );
      }

      return (
        <Box
          sx={{
            position: 'relative',
            width: 200,
            height: 160,
            mx: 'auto',
            mb: 2,
          }}
        >
          {/* Tick marks */}
          <svg width="200" height="140" viewBox="0 0 200 140">
            {ticks}
          </svg>
          {/* Center text */}
          <Box
            sx={{
              position: 'absolute',
              top: '45%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h2" fontWeight={600} sx={{ lineHeight: 1 }}>
              0
            </Typography>
            <Typography variant="body2" color="text.secondary">
              of {maxVal} photos
            </Typography>
          </Box>
          {/* Minus button */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 10,
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: '#E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#D1D5DB' },
            }}
          >
            <Typography variant="h5" color="#9CA3AF" sx={{ lineHeight: 1, mt: -0.5 }}>
              −
            </Typography>
          </Box>
          {/* Plus button */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 10,
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: '#C2410C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#9A3412' },
            }}
          >
            <Typography variant="h5" color="#FFFFFF" sx={{ lineHeight: 1, mt: -0.5 }}>
              +
            </Typography>
          </Box>
        </Box>
      );
    };

    // Render Upload Image specific fields
    const renderUploadImageFields = () => (
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 2,
          p: 3,
          border: '1px solid #EFF0F0',
          minWidth: 280,
        }}
      >
        {renderProgressDial()}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Unit of measurement <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Select
            fullWidth
            value={uploadUnitOfMeasurement}
            onChange={(e) => setUploadUnitOfMeasurement(e.target.value)}
            sx={{ bgcolor: '#FFFFFF' }}
          >
            <MenuItem value="Upload Photos">Upload Photos</MenuItem>
            <MenuItem value="Upload Documents">Upload Documents</MenuItem>
            <MenuItem value="Upload Files">Upload Files</MenuItem>
          </Select>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Max value <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            value={uploadMaxValue}
            onChange={(e) => setUploadMaxValue(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Completion threshold <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            value={uploadCompletionThreshold}
            onChange={(e) => setUploadCompletionThreshold(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
          />
        </Box>

        <Box
          sx={{
            bgcolor: '#F9FAFB',
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Modal Header <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              value={uploadModalHeader}
              onChange={(e) => setUploadModalHeader(e.target.value)}
              placeholder="Header label"
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
            />
          </Box>

          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Description: <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={uploadModalDescription}
              onChange={(e) => setUploadModalDescription(e.target.value)}
              placeholder="How to upload"
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: '#F9FAFB',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            JSON criteria schema
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={uploadJsonSchema}
            onChange={(e) => setUploadJsonSchema(e.target.value)}
            placeholder="Enter JSON here"
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
          />
        </Box>
      </Box>
    );

    return (
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Main Form */}
        <Box
          sx={{
            flex: 1,
            bgcolor: '#FFFFFF',
            borderRadius: 2,
            p: 3,
            border: '1px solid #EFF0F0',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
            <Typography variant="h5" fontWeight={600}>
              Create new action
            </Typography>
            <Chip
              label={
                createActionCollection === 'Not Assigned' ? 'Not Assigned' : createActionCollection
              }
              size="small"
              onClick={() => {}}
              onDelete={() => {}}
              deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
              sx={{
                bgcolor:
                  createActionCollection === 'Not Assigned'
                    ? '#F7F5F2'
                    : theme.palette.accentsYellowA.light,
                color:
                  createActionCollection === 'Not Assigned'
                    ? theme.palette.textDarkPrimary.primary
                    : theme.palette.accentsYellowA.dark,
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: 'inherit',
                },
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            TASK ID {createTaskId} &bull; {selectedCompletionType?.toUpperCase()} &bull;{' '}
            {inputTypeLabel}
          </Typography>

          {/* Form Fields */}
          <Box>
            {/* Title and Priority */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Title <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  value={createTitle}
                  onChange={(e) => setCreateTitle(e.target.value)}
                  placeholder="Action Title"
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
                />
              </Box>
              <Box sx={{ width: 120 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Priority level <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  value={createPriority}
                  onChange={(e) => setCreatePriority(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
                />
              </Box>
            </Box>

            {/* Description */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Description: <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="Description of action"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
              />
            </Box>

            {/* Secondary CTA */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Secondary CTA <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={createSecondaryCta}
                onChange={(e) => setCreateSecondaryCta(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
              />
            </Box>

            {/* Link */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Link <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={createLink}
                onChange={(e) => setCreateLink(e.target.value)}
                placeholder="Paste link"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon sx={{ color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
              />
            </Box>

            {/* Dates */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Start date <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  value={createStartDate}
                  onChange={(e) => setCreateStartDate(e.target.value)}
                  placeholder="MM/DD/YY"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 180, '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
                />
              </Box>
              <Typography sx={{ pb: 1 }}>—</Typography>
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Expiry date <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  value={createExpiryDate}
                  onChange={(e) => setCreateExpiryDate(e.target.value)}
                  placeholder="MM/DD/YY"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 180, '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
                />
              </Box>
            </Box>

            {/* Repeats */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Repeats <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Switch
                  checked={createRecurring}
                  onChange={(e) => setCreateRecurring(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: theme.palette.accentsYellowA.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: theme.palette.accentsYellowA.main,
                    },
                  }}
                />
                <Typography variant="body2">{createRecurring ? 'Yes' : 'No'}</Typography>
                {createRecurring && (
                  <Select
                    value={createRecurringInterval}
                    onChange={(e) => setCreateRecurringInterval(e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{ minWidth: 120, bgcolor: '#FFFFFF' }}
                  >
                    <MenuItem value="" disabled>
                      Select
                    </MenuItem>
                    {recurringIntervalOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Box>
            </Box>

            {/* Cohorts */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Cohorts
              </Typography>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Assign cohort(s)
              </Typography>
              <Select
                value=""
                onChange={(e) => handleAddCreateCohort(e.target.value)}
                displayEmpty
                fullWidth
                sx={{ bgcolor: '#FFFFFF', mb: 2 }}
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                {availableCohorts
                  .filter((c) => !createCohorts.includes(c))
                  .map((cohort) => (
                    <MenuItem key={cohort} value={cohort}>
                      {cohort}
                    </MenuItem>
                  ))}
              </Select>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {createCohorts.map((cohort) => (
                  <Chip
                    key={cohort}
                    label={cohort}
                    onDelete={() => handleRemoveCreateCohort(cohort)}
                    sx={{
                      bgcolor: '#F7F5F2',
                      '& .MuiChip-deleteIcon': { color: '#6B7280' },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Rewards */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Rewards
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Reward type <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Select
                    value={createRewardType}
                    onChange={(e) => setCreateRewardType(e.target.value)}
                    sx={{ width: 180, bgcolor: '#FFFFFF' }}
                  >
                    {rewardTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Reward amount <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                    value={createRewardAmount}
                    onChange={(e) => setCreateRewardAmount(e.target.value)}
                    placeholder="$0.00"
                    sx={{ width: 180, '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' } }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleSaveNewAction}
                disabled={!isCreateFormValid()}
                sx={{
                  bgcolor: isCreateFormValid() ? theme.palette.accentsYellowA.main : '#E0E0E0',
                  color: isCreateFormValid() ? theme.palette.textDarkPrimary.primary : '#9CA3AF',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    bgcolor: isCreateFormValid() ? theme.palette.accentsYellowA.dark : '#E0E0E0',
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#E0E0E0',
                    color: '#9CA3AF',
                  },
                }}
              >
                Create action
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancelCreateAction}
                sx={{
                  borderColor: '#E0E0E0',
                  color: theme.palette.textDarkPrimary.primary,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Right side panel for Upload Image */}
        {isUploadImage && renderUploadImageFields()}
      </Box>
    );
  };

  const renderAllActionsTable = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Priority &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Task ID &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Title &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Completion Type &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Reward Type &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Amount &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Start Date &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Expiry Date &#8645;
          </TableCell>
          <TableCell sx={{ width: 40 }}></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {currentActions.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                No actions available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isMasterTenant
                  ? 'Click "Create new" to add your first action.'
                  : 'Click "Import from Catalog" to import actions from Master tenant.'}
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          currentActions.map((action) => (
            <TableRow key={action.id} hover>
              <TableCell>{action.priority}</TableCell>
              <TableCell>{action.taskId}</TableCell>
              <TableCell sx={{ maxWidth: 200 }}>{action.title}</TableCell>
              <TableCell>{action.completionType}</TableCell>
              <TableCell>{action.rewardType}</TableCell>
              <TableCell>{action.amount}</TableCell>
              <TableCell>{action.startDate}</TableCell>
              <TableCell>{action.expiryDate}</TableCell>
              <TableCell>
                <IconButton size="small" onClick={(e) => handleRowMenuOpen(e, action.id)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  const renderCollectionsTable = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Category &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Collection Name &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Assigned Actions &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Enrolled &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Cohort(s) &#8645;
          </TableCell>
          <TableCell sx={{ fontWeight: 600, color: '#6B7280', fontSize: 13 }}>
            Avg. Completion &#8645;
          </TableCell>
          <TableCell sx={{ width: 40 }}></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {collections.map((collection) => (
          <TableRow key={collection.id} hover>
            <TableCell>{collection.category}</TableCell>
            <TableCell>{collection.collectionName}</TableCell>
            <TableCell>{collection.assignedActions}</TableCell>
            <TableCell>{collection.enrolled}</TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {collection.cohorts.map((cohort, idx) => (
                  <Chip
                    key={idx}
                    label={cohort}
                    size="small"
                    sx={{
                      bgcolor: '#F5F5F5',
                      fontSize: 12,
                      height: 24,
                    }}
                  />
                ))}
              </Box>
            </TableCell>
            <TableCell>{collection.avgCompletion}</TableCell>
            <TableCell>
              <IconButton size="small">
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderCreateCollectionForm = () => (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: 2,
        p: 3,
        border: '1px solid #EFF0F0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Create new collection
        </Typography>
        <Chip
          label={newCollectionCategory}
          onDelete={() => {}}
          deleteIcon={<span style={{ fontSize: 12, marginLeft: 4 }}>&#9660;</span>}
          sx={{
            bgcolor: theme.palette.accentsYellowA.light,
            color: theme.palette.accentsYellowA.dark,
            fontWeight: 500,
            '& .MuiChip-deleteIcon': {
              color: theme.palette.accentsYellowA.dark,
            },
          }}
          onClick={() => {}}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
          Title <span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          value={newCollectionTitle}
          onChange={(e) => setNewCollectionTitle(e.target.value)}
          placeholder="Enter collection title"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#FFFFFF',
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
          Description:
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={newCollectionDescription}
          onChange={(e) => setNewCollectionDescription(e.target.value)}
          placeholder="Enter description"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#FFFFFF',
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Cohorts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Assign cohort(s)
        </Typography>
        <Select
          size="small"
          displayEmpty
          value=""
          onChange={(e) => handleAddCohort(e.target.value as string)}
          sx={{ minWidth: 200, mb: 2 }}
        >
          <MenuItem value="" disabled>
            Select
          </MenuItem>
          {availableCohorts
            .filter((c) => !selectedCohorts.includes(c))
            .map((cohort) => (
              <MenuItem key={cohort} value={cohort}>
                {cohort}
              </MenuItem>
            ))}
        </Select>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedCohorts.map((cohort) => (
            <Chip
              key={cohort}
              label={cohort}
              onDelete={() => handleRemoveCohort(cohort)}
              sx={{
                bgcolor: '#F5F5F5',
                '& .MuiChip-deleteIcon': {
                  fontSize: 18,
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleCreateCollection}
          disabled={!newCollectionTitle.trim()}
          sx={{
            bgcolor: theme.palette.accentsYellowA.main,
            color: theme.palette.textDarkPrimary.primary,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              bgcolor: theme.palette.accentsYellowA.dark,
            },
          }}
        >
          Create Collection
        </Button>
        <Button
          variant="outlined"
          onClick={handleCancelCreate}
          sx={{
            borderColor: '#E0E0E0',
            color: theme.palette.textDarkPrimary.primary,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 3, fontFamily: 'Manrope, sans-serif' }}>
      {/* Breadcrumb */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {getBreadcrumb()}
      </Typography>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Actions
        </Typography>
        {!editMode && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleOpenCsvImport}
              sx={{
                borderColor: '#E0E0E0',
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Import
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleExportActions}
              sx={{
                borderColor: '#E0E0E0',
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Export
            </Button>
            {isMasterTenant ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNewMenuOpen}
                sx={{
                  bgcolor: theme.palette.accentsYellowA.main,
                  color: theme.palette.textDarkPrimary.primary,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: theme.palette.accentsYellowA.dark,
                  },
                }}
              >
                Create new
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleOpenImportDialog}
                sx={{
                  bgcolor: theme.palette.accentsYellowA.main,
                  color: theme.palette.textDarkPrimary.primary,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: theme.palette.accentsYellowA.dark,
                  },
                }}
              >
                Import from Catalog
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 14,
              color: '#6B7280',
              '&.Mui-selected': {
                color: theme.palette.textDarkPrimary.primary,
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              bgcolor: theme.palette.textDarkPrimary.primary,
            },
          }}
        >
          <Tab label="All Actions" />
          <Tab label="Collections" />
          <Tab label="History" />
        </Tabs>
      </Box>

      {/* Content */}
      {isCreatingAction ? (
        renderCreateActionForm()
      ) : editMode ? (
        renderEditActionForm()
      ) : activeTab === 1 && isCreating ? (
        renderCreateCollectionForm()
      ) : (
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: 2,
            border: '1px solid #EFF0F0',
            overflow: 'hidden',
          }}
        >
          {/* Search and Filter */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9CA3AF' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#FFFFFF',
                },
              }}
            />
            <Button
              startIcon={<FilterListIcon />}
              onClick={handleFilterOpen}
              sx={{
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Filter
            </Button>
          </Box>

          {/* Table */}
          <Box sx={{ overflowX: 'auto' }}>
            {activeTab === 0 && renderAllActionsTable()}
            {activeTab === 1 && renderCollectionsTable()}
            {activeTab === 2 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No history available</Typography>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderTop: '1px solid #EFF0F0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Show[{'{'}table data{'}'}]
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                sx={{ minWidth: 100 }}
              >
                <MenuItem value={10}>10 per page</MenuItem>
                <MenuItem value={25}>25 per page</MenuItem>
                <MenuItem value={50}>50 per page</MenuItem>
              </Select>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {activeTab === 0 ? '1-25 of 100' : `1-25 of ${collections.length}`}
              </Typography>
              <Pagination
                count={activeTab === 0 ? 4 : 1}
                page={page}
                onChange={(_, value) => setPage(value)}
                size="small"
                siblingCount={0}
                boundaryCount={1}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Row Action Menu */}
      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={handleRowMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEditAction}>
          <EditOutlinedIcon sx={{ mr: 1.5, fontSize: 20, color: '#6B7280' }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicateAction}>
          <ContentCopyIcon sx={{ mr: 1.5, fontSize: 20, color: '#6B7280' }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleRowMenuClose}>
          <RemoveCircleOutlineIcon sx={{ mr: 1.5, fontSize: 20, color: '#6B7280' }} />
          Deprecate
        </MenuItem>
        <MenuItem onClick={handleRowMenuClose}>
          <RestoreIcon sx={{ mr: 1.5, fontSize: 20, color: '#6B7280' }} />
          Restore
        </MenuItem>
        <MenuItem onClick={handleRowMenuClose}>
          <DeleteOutlineIcon sx={{ mr: 1.5, fontSize: 20, color: '#6B7280' }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 480,
            maxHeight: 500,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          },
        }}
      >
        {/* Filter Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid #EFF0F0',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Filters
          </Typography>
          <IconButton size="small" onClick={handleFilterClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Filter Content */}
        <Box sx={{ display: 'flex', minHeight: 280 }}>
          {/* Filter Categories */}
          <Box
            sx={{
              width: 180,
              borderRight: '1px solid #EFF0F0',
              bgcolor: '#FAFAFA',
            }}
          >
            <List disablePadding>
              {['Completion type', 'Reward type', 'Date created', 'Collections', 'Cohorts'].map(
                (category) => (
                  <ListItemButton
                    key={category}
                    selected={activeFilterCategory === category}
                    onClick={() => handleFilterCategoryClick(category)}
                    sx={{
                      py: 1.5,
                      '&.Mui-selected': {
                        bgcolor: '#FFFFFF',
                        borderRight: '2px solid',
                        borderRightColor: theme.palette.accentsYellowA.main,
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: '#FFFFFF',
                      },
                    }}
                  >
                    <ListItemText
                      primary={category}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: activeFilterCategory === category ? 600 : 400,
                      }}
                    />
                    <ChevronRightIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
                  </ListItemButton>
                )
              )}
            </List>
          </Box>

          {/* Filter Options */}
          <Box sx={{ flex: 1, p: 2 }}>
            {/* Completion Type Options */}
            {activeFilterCategory === 'Completion type' && (
              <Box>
                {['Fitness Data Tracking', 'Self Report', 'Hybrid'].map((type) => (
                  <Box
                    key={type}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.5,
                    }}
                  >
                    <Checkbox
                      checked={filterCompletionTypes.includes(type)}
                      onChange={() => handleCompletionTypeToggle(type)}
                      sx={{
                        color: '#D1D5DB',
                        '&.Mui-checked': {
                          color: theme.palette.accentsYellowA.main,
                        },
                      }}
                    />
                    <Typography variant="body2">{type}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Reward Type Options */}
            {activeFilterCategory === 'Reward type' && (
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Value
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={filterRewardType}
                  onChange={(e) => setFilterRewardType(e.target.value)}
                  displayEmpty
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Dollars">Dollars</MenuItem>
                  <MenuItem value="Entries">Entries</MenuItem>
                  <MenuItem value="Points">Points</MenuItem>
                </Select>

                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Min
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={filterRewardMin}
                  onChange={(e) => setFilterRewardMin(e.target.value)}
                  placeholder="$0.00"
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Max
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={filterRewardMax}
                  onChange={(e) => setFilterRewardMax(e.target.value)}
                  placeholder="$25.00"
                />
              </Box>
            )}

            {/* Date Created Options */}
            {activeFilterCategory === 'Date created' && (
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Show actions from
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={filterDatePreset}
                  onChange={(e) => setFilterDatePreset(e.target.value)}
                  displayEmpty
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Past 7 days">Past 7 days</MenuItem>
                  <MenuItem value="Past 30 days">Past 30 days</MenuItem>
                  <MenuItem value="Past 90 days">Past 90 days</MenuItem>
                  <MenuItem value="Custom">Custom</MenuItem>
                </Select>

                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  From
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  placeholder="MM/DD/YY"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  To
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  placeholder="MM/DD/YY"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            {/* Collections Options */}
            {activeFilterCategory === 'Collections' && (
              <Box>
                {['Fitness & exercise', 'Healthy eating', 'Health & wellness'].map((collection) => (
                  <Box
                    key={collection}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.5,
                    }}
                  >
                    <Checkbox
                      checked={filterCollections.includes(collection)}
                      onChange={() => handleCollectionToggle(collection)}
                      sx={{
                        color: '#D1D5DB',
                        '&.Mui-checked': {
                          color: theme.palette.accentsYellowA.main,
                        },
                      }}
                    />
                    <Typography variant="body2">{collection}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Cohorts Options */}
            {activeFilterCategory === 'Cohorts' && (
              <Box>
                {['Fitness & exercise', 'Healthy eating', 'Health & wellness'].map((cohort) => (
                  <Box
                    key={cohort}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.5,
                    }}
                  >
                    <Checkbox
                      checked={filterCohortsList.includes(cohort)}
                      onChange={() => handleCohortFilterToggle(cohort)}
                      sx={{
                        color: '#D1D5DB',
                        '&.Mui-checked': {
                          color: theme.palette.accentsYellowA.main,
                        },
                      }}
                    />
                    <Typography variant="body2">{cohort}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Filter Footer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderTop: '1px solid #EFF0F0',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Results: {getFilterResultsCount()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              sx={{
                borderColor: '#E0E0E0',
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                bgcolor: theme.palette.accentsYellowA.main,
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: theme.palette.accentsYellowA.dark,
                },
              }}
            >
              Apply filters
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Duplicate Confirmation Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={handleCancelDuplicate}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>
              Duplicate Action
            </Typography>
            <IconButton size="small" onClick={handleCancelDuplicate}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            Are you sure you want to duplicate <strong>{actionToDuplicate?.title}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            A new action will be created with the same details and the next available priority
            level.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancelDuplicate}
            sx={{
              borderColor: '#E0E0E0',
              color: theme.palette.textDarkPrimary.primary,
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDuplicate}
            sx={{
              bgcolor: theme.palette.accentsYellowA.main,
              color: theme.palette.textDarkPrimary.primary,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                bgcolor: theme.palette.accentsYellowA.dark,
              },
            }}
          >
            Yes, Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Import Template Dialog */}
      <Dialog
        open={csvImportStep === 'template'}
        onClose={handleCloseCsvImport}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>
              Download Import Template
            </Typography>
            <IconButton size="small" onClick={handleCloseCsvImport}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Download our template to match the required format. Fill in your Actions and upload the
            file. It's quick and easy.
          </Typography>

          <Box
            sx={{
              bgcolor: '#FFFBEB',
              borderRadius: 2,
              p: 2,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LightbulbOutlinedIcon
                sx={{ color: theme.palette.accentsYellowA.dark, fontSize: 20, mt: 0.25 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Tips for preparing your file
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Don't change the columns headers
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Ensure columns marked "Required" are filled out
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Use date format MM-DD-YYYY
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleDownloadTemplate}
            sx={{
              borderColor: '#E0E0E0',
              color: theme.palette.textDarkPrimary.primary,
              textTransform: 'none',
              fontWeight: 500,
              py: 1.5,
              mb: 2,
            }}
          >
            Download template
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={handleProceedToUpload}
            sx={{
              bgcolor: theme.palette.accentsYellowA.main,
              color: theme.palette.textDarkPrimary.primary,
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              '&:hover': {
                bgcolor: theme.palette.accentsYellowA.dark,
              },
            }}
          >
            Continue to Upload
          </Button>
        </DialogContent>
      </Dialog>

      {/* Upload CSV File Dialog */}
      <Dialog
        open={csvImportStep === 'upload'}
        onClose={handleCloseCsvImport}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>
              Upload CSV File
            </Typography>
            <IconButton size="small" onClick={handleCloseCsvImport}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!uploadedFile ? (
            <Box
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              sx={{
                border: '2px dashed #D1D5DB',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.accentsYellowA.main,
                  bgcolor: '#FFFBEB',
                },
              }}
              onClick={() => document.getElementById('csv-file-input')?.click()}
            >
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileSelect}
              />
              <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: '#9CA3AF', mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                Click to upload, or drag and drop your .csv file here
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maximum size is 10MB
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  border: '1px solid #EFF0F0',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <DescriptionOutlinedIcon sx={{ color: '#6B7280' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {uploadedFile.name}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={handleRemoveFile}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              {(isUploading || uploadComplete) && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {uploadComplete ? 'Upload complete' : 'Uploading ...'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {uploadProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: '#EFF0F0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.accentsYellowA.main,
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={handleCsvImportComplete}
            disabled={!uploadComplete}
            sx={{
              bgcolor: uploadComplete ? theme.palette.accentsYellowA.main : '#E0E0E0',
              color: uploadComplete ? theme.palette.textDarkPrimary.primary : '#9CA3AF',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              mt: 2,
              '&:hover': {
                bgcolor: uploadComplete ? theme.palette.accentsYellowA.dark : '#E0E0E0',
              },
              '&.Mui-disabled': {
                bgcolor: '#E0E0E0',
                color: '#9CA3AF',
              },
            }}
          >
            Import
          </Button>
        </DialogContent>
      </Dialog>

      {/* Export Actions Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Export Actions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Download a .csv of {currentActions.length} action
                {currentActions.length !== 1 ? 's' : ''} in this view
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseExportDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Select options
          </Typography>

          {/* Select All */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
              borderBottom: '1px solid #EFF0F0',
              mb: 1,
            }}
          >
            <Checkbox
              checked={exportColumns.length === allExportColumns.length}
              indeterminate={
                exportColumns.length > 0 && exportColumns.length < allExportColumns.length
              }
              onChange={handleToggleAllExportColumns}
              sx={{
                color: '#D1D5DB',
                '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                  color: theme.palette.accentsYellowA.main,
                },
              }}
            />
            <Typography variant="body2" fontWeight={500}>
              Select all
            </Typography>
          </Box>

          {/* Column Options */}
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {allExportColumns.map((column) => (
              <Box
                key={column}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 0.25,
                }}
              >
                <Checkbox
                  checked={exportColumns.includes(column)}
                  onChange={() => handleToggleExportColumn(column)}
                  sx={{
                    color: '#D1D5DB',
                    '&.Mui-checked': {
                      color: theme.palette.accentsYellowA.main,
                    },
                  }}
                />
                <Typography variant="body2">{column}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {exportColumns.length}/{allExportColumns.length} columns selected
          </Typography>
          <Button
            variant="contained"
            onClick={handleExportToCsv}
            disabled={exportColumns.length === 0}
            sx={{
              bgcolor: theme.palette.accentsYellowA.main,
              color: theme.palette.textDarkPrimary.primary,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                bgcolor: theme.palette.accentsYellowA.dark,
              },
              '&.Mui-disabled': {
                bgcolor: '#E0E0E0',
                color: '#9CA3AF',
              },
            }}
          >
            Export to csv
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import from Catalog Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>
              Import from Catalog
            </Typography>
            <IconButton size="small" onClick={handleCloseImportDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select actions to import from the Master tenant catalog
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {Object.entries(masterActionsByCategory).map(([category, actions]) => {
            const isExpanded = expandedImportCategories.includes(category);
            const isFullySelected = isCategoryFullySelected(category);
            const isPartiallySelected = isCategoryPartiallySelected(category);

            return (
              <Box key={category} sx={{ borderBottom: '1px solid #EFF0F0' }}>
                {/* Category Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#FAFAFA' },
                  }}
                  onClick={() => handleToggleImportCategory(category)}
                >
                  <Checkbox
                    checked={isFullySelected}
                    indeterminate={isPartiallySelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleImportCategoryAll(category);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: theme.palette.accentsYellowA.main,
                      },
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight={500}>
                      {category}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {actions.length} action{actions.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <ExpandMoreIcon
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      color: '#9CA3AF',
                    }}
                  />
                </Box>

                {/* Actions List */}
                <Collapse in={isExpanded}>
                  <Box sx={{ bgcolor: '#FAFAFA' }}>
                    {actions.map((action) => (
                      <Box
                        key={action.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 2,
                          py: 1,
                          pl: 4,
                          borderTop: '1px solid #EFF0F0',
                          '&:hover': { bgcolor: '#F5F5F5' },
                        }}
                      >
                        <Checkbox
                          checked={selectedImportActions.includes(action.id)}
                          onChange={() => handleToggleImportAction(action.id)}
                          sx={{
                            color: '#D1D5DB',
                            '&.Mui-checked': {
                              color: theme.palette.accentsYellowA.main,
                            },
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {action.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {action.completionType} &bull; {action.rewardType} {action.amount}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedImportActions.length} action{selectedImportActions.length !== 1 ? 's' : ''}{' '}
            selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleCloseImportDialog}
              sx={{
                borderColor: '#E0E0E0',
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleImportActions}
              disabled={selectedImportActions.length === 0}
              sx={{
                bgcolor: theme.palette.accentsYellowA.main,
                color: theme.palette.textDarkPrimary.primary,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  bgcolor: theme.palette.accentsYellowA.dark,
                },
                '&.Mui-disabled': {
                  bgcolor: '#E0E0E0',
                  color: '#9CA3AF',
                },
              }}
            >
              Import Selected
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Create new Menu */}
      <Menu
        anchorEl={createNewMenuAnchor}
        open={Boolean(createNewMenuAnchor)}
        onClose={handleCreateNewMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 160,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={handleCreateNewAction}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: 14,
            '&:hover': { bgcolor: '#F7F5F2' },
          }}
        >
          Action
        </MenuItem>
        <MenuItem
          onClick={handleCreateNewCollection}
          sx={{
            py: 1.5,
            px: 2,
            fontSize: 14,
            '&:hover': { bgcolor: '#F7F5F2' },
          }}
        >
          Collection
        </MenuItem>
      </Menu>

      {/* Create new action Dialog - Completion Type Selection */}
      <Dialog
        open={createActionDialogOpen}
        onClose={handleCloseCreateActionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Create new action
            </Typography>
            <IconButton onClick={handleCloseCreateActionDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select the completion type for this action
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Self Report Option */}
            <Box
              onClick={() => handleSelectCompletionType('Self Report')}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                border: '1px solid #E0E0E0',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.accentsYellowA.main,
                  bgcolor: 'rgba(251, 191, 36, 0.05)',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: '#F0FDF4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AssignmentTurnedInOutlinedIcon sx={{ color: '#22C55E', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  Self Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manually enter input to confirm task completion
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: '#9CA3AF', alignSelf: 'center' }} />
            </Box>

            {/* Fitness Data Tracking Option */}
            <Box
              onClick={() => handleSelectCompletionType('Fitness Data Tracking')}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                border: '1px solid #E0E0E0',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.accentsYellowA.main,
                  bgcolor: 'rgba(251, 191, 36, 0.05)',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: '#EEF2FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <ShowChartIcon sx={{ color: '#6366F1', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  Fitness Data Tracking
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Auto-tracking using connected apps or devices
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: '#9CA3AF', alignSelf: 'center' }} />
            </Box>

            {/* Hybrid Option */}
            <Box
              onClick={() => handleSelectCompletionType('Hybrid')}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                border: '1px solid #E0E0E0',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.accentsYellowA.main,
                  bgcolor: 'rgba(251, 191, 36, 0.05)',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: '#FEF3C7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <MergeTypeIcon sx={{ color: '#F59E0B', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  Hybrid
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Combination of user input and auto-tracking
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: '#9CA3AF', alignSelf: 'center' }} />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Self Report Input Type Dialog */}
      <Dialog
        open={selfReportDialogOpen}
        onClose={handleCloseSelfReportDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Self report action
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select input type
              </Typography>
            </Box>
            <IconButton onClick={handleCloseSelfReportDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Self Attest Option */}
            <Box
              onClick={() => handleSelectInputType('Self Attest')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: selectedInputType === 'Self Attest' ? '2px solid' : '1px solid',
                borderColor:
                  selectedInputType === 'Self Attest'
                    ? theme.palette.accentsYellowA.main
                    : '#E0E0E0',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                bgcolor:
                  selectedInputType === 'Self Attest' ? 'rgba(251, 191, 36, 0.05)' : 'transparent',
                '&:hover': {
                  borderColor: theme.palette.accentsYellowA.main,
                  bgcolor: 'rgba(251, 191, 36, 0.05)',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: theme.palette.accentsYellowA.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CheckBoxOutlinedIcon
                  sx={{ color: theme.palette.accentsYellowA.dark, fontSize: 24 }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  Self Attest
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date selection and self attestation checkbox
                </Typography>
              </Box>
            </Box>

            {/* Upload Image Option */}
            <Box
              onClick={() => handleSelectInputType('Upload Image')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: selectedInputType === 'Upload Image' ? '2px solid' : '1px solid',
                borderColor:
                  selectedInputType === 'Upload Image'
                    ? theme.palette.accentsYellowA.main
                    : '#E0E0E0',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                bgcolor:
                  selectedInputType === 'Upload Image' ? 'rgba(251, 191, 36, 0.05)' : 'transparent',
                '&:hover': {
                  borderColor: theme.palette.accentsYellowA.main,
                  bgcolor: 'rgba(251, 191, 36, 0.05)',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: theme.palette.accentsYellowA.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PhotoCameraOutlinedIcon
                  sx={{ color: theme.palette.accentsYellowA.dark, fontSize: 24 }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  Upload Image
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload image as progress toward completion
                </Typography>
              </Box>
            </Box>

            {/* Progress Dial Option */}
            <Box
              onClick={() => handleSelectInputType('Progress Dial')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: selectedInputType === 'Progress Dial' ? '2px solid' : '1px solid',
                borderColor:
                  selectedInputType === 'Progress Dial'
                    ? theme.palette.accentsYellowA.main
                    : '#E0E0E0',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                bgcolor:
                  selectedInputType === 'Progress Dial'
                    ? 'rgba(251, 191, 36, 0.05)'
                    : 'transparent',
                '&:hover': {
                  borderColor: theme.palette.accentsYellowA.main,
                  bgcolor: 'rgba(251, 191, 36, 0.05)',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: theme.palette.accentsYellowA.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <DonutLargeIcon sx={{ color: theme.palette.accentsYellowA.dark, fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  Progress Dial
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose between incremental or input progress dial
                </Typography>
              </Box>
            </Box>

            {/* Completion date Option */}
            <Box
              onClick={() => handleSelectInputType('Completion date')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: selectedInputType === 'Completion date' ? '2px solid' : '1px solid',
                borderColor:
                  selectedInputType === 'Completion date'
                    ? theme.palette.accentsYellowA.main
                    : '#E0E0E0',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                bgcolor:
                  selectedInputType === 'Completion date'
                    ? 'rgba(251, 191, 36, 0.05)'
                    : 'transparent',
                '&:hover': {
                  borderColor: theme.palette.accentsYellowA.main,
                  bgcolor: 'rgba(251, 191, 36, 0.05)',
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: theme.palette.accentsYellowA.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <EventIcon sx={{ color: theme.palette.accentsYellowA.dark, fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  Completion date
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Define a completion date
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleBackToCompletionType}
            sx={{
              flex: 1,
              borderColor: '#E0E0E0',
              color: theme.palette.textDarkPrimary.primary,
              textTransform: 'none',
              fontWeight: 500,
              py: 1.5,
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSelfReportAction}
            disabled={!selectedInputType}
            sx={{
              flex: 1,
              bgcolor: selectedInputType ? theme.palette.accentsYellowA.main : '#E0E0E0',
              color: selectedInputType ? theme.palette.textDarkPrimary.primary : '#9CA3AF',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              '&:hover': {
                bgcolor: selectedInputType ? theme.palette.accentsYellowA.dark : '#E0E0E0',
              },
              '&.Mui-disabled': {
                bgcolor: '#E0E0E0',
                color: '#9CA3AF',
              },
            }}
          >
            Create action
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: '#FFFFFF',
            px: 2,
            py: 1.5,
            borderRadius: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #E0E0E0',
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: '#22C55E' }} />
          <Typography variant="body2" fontWeight={500}>
            {snackbarMessage}
          </Typography>
          <IconButton size="small" onClick={() => setSnackbarOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>
    </Box>
  );
};

export default PrototypeActions;
